import { useCallback, useEffect, useState } from "react";
import { Loader2, Plus, ShieldCheck, Trash2, UserCog } from "lucide-react";
import { AdminNav } from "@/components/admin/AdminNav";
import { AdminAccessDenied } from "@/components/admin/AdminAccessDenied";
import { AdminLoading, useAdminAuth } from "@/components/admin/useAdminAuth";

const API = process.env.REACT_APP_BACKEND_URL;
const ROLES = ["SUPER_ADMIN", "RECRUITMENT_ADMIN", "CONTENT_ADMIN", "SUPPORT_ADMIN"];
const EMPTY = { name: "", email: "", password: "", admin_role: "SUPPORT_ADMIN" };

const roleLabel = (role) => role.replace(/_/g, " ");

export default function AdminUsers() {
    const { user, logout, hasPermission } = useAdminAuth();
    const [users, setUsers] = useState(null);
    const [form, setForm] = useState(EMPTY);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [message, setMessage] = useState("");
    const [resetFor, setResetFor] = useState(null);
    const [resetPassword, setResetPassword] = useState("");

    const load = useCallback(async () => {
        const res = await fetch(`${API}/api/admin/users`, { credentials: "include" });
        if (res.ok) setUsers(await res.json());
    }, []);

    useEffect(() => {
        const allowed = Boolean(user?.permissions?.includes("*") || user?.permissions?.includes("users"));
        if (user && allowed) load();
    }, [user, load]);

    if (!user) return <AdminLoading />;

    const run = async (fn, success) => {
        setError("");
        setMessage("");
        setBusy(true);
        try {
            await fn();
            setMessage(success);
            setForm(EMPTY);
            setResetFor(null);
            setResetPassword("");
            await load();
        } catch (err) {
            setError(err.message || "Action failed.");
        } finally {
            setBusy(false);
        }
    };

    const parseError = async (res, fallback) => {
        const body = await res.json().catch(() => ({}));
        throw new Error(typeof body.detail === "string" ? body.detail : fallback);
    };

    const createUser = (e) => {
        e.preventDefault();
        run(async () => {
            const res = await fetch(`${API}/api/admin/users`, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(form),
            });
            if (!res.ok) await parseError(res, "Could not create admin user.");
        }, "Admin user created.");
    };

    const updateUser = (id, payload) => run(async () => {
        const res = await fetch(`${API}/api/admin/users/${id}`, {
            method: "PATCH",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
        });
        if (!res.ok) await parseError(res, "Could not update admin user.");
    }, "Admin user updated.");

    const removeUser = (id) => {
        if (!window.confirm("Remove this administrator? Their sessions will be revoked immediately.")) return;
        run(async () => {
            const res = await fetch(`${API}/api/admin/users/${id}`, { method: "DELETE", credentials: "include" });
            if (!res.ok) await parseError(res, "Could not remove admin user.");
        }, "Admin user removed.");
    };

    const resetUserPassword = (e) => {
        e.preventDefault();
        run(async () => {
            const res = await fetch(`${API}/api/admin/users/${resetFor}/reset-password`, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ password: resetPassword }),
            });
            if (!res.ok) await parseError(res, "Could not reset password.");
        }, "Password reset and existing sessions revoked.");
    };

    return (
        <div className="min-h-screen bg-[#F8FAFC] font-adminSans text-[#0F172A]">
            <AdminNav active="users" email={user.email} user={user} onLogout={logout} />
            {!hasPermission("users") ? <AdminAccessDenied section="admin user management" /> : (
                <main className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8" data-testid="admin-users-page">
                    <div className="flex flex-wrap items-end justify-between gap-5">
                        <div>
                            <p className="text-xs font-bold uppercase tracking-[0.24em] text-[#D4AF37]">Highest Authority Only</p>
                            <h1 className="mt-2 font-adminDisplay text-4xl font-bold tracking-tight">Users / Admins</h1>
                            <p className="mt-2 max-w-2xl text-sm text-[#64748B]">Create administrators, assign least-privilege roles, disable access and reset credentials.</p>
                        </div>
                    </div>

                    <section className="mt-8 rounded-lg border border-[#E2E8F0] bg-white p-6 shadow-sm" data-testid="admin-user-create-panel">
                        <h2 className="flex items-center gap-2 font-adminDisplay text-2xl font-semibold"><Plus size={20} className="text-[#D4AF37]" /> Create Admin User</h2>
                        <form onSubmit={createUser} className="mt-6 grid gap-4 lg:grid-cols-5">
                            <input data-testid="new-admin-name" required placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-md border border-[#E2E8F0] px-4 py-3 text-sm outline-none focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/30" />
                            <input data-testid="new-admin-email" required type="email" placeholder="Email address" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-md border border-[#E2E8F0] px-4 py-3 text-sm outline-none focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/30" />
                            <input data-testid="new-admin-password" required type="password" minLength={12} placeholder="Temporary password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="rounded-md border border-[#E2E8F0] px-4 py-3 text-sm outline-none focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/30" autoComplete="new-password" />
                            <select data-testid="new-admin-role" value={form.admin_role} onChange={(e) => setForm({ ...form, admin_role: e.target.value })} className="rounded-md border border-[#E2E8F0] px-4 py-3 text-sm outline-none focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/30">
                                {ROLES.map((role) => <option key={role} value={role}>{roleLabel(role)}</option>)}
                            </select>
                            <button type="submit" data-testid="create-admin-button" disabled={busy} className="rounded-md bg-[#0B1120] px-5 py-3 text-xs font-bold uppercase tracking-[0.14em] text-white transition-colors hover:bg-[#1E293B] disabled:opacity-60">Create</button>
                        </form>
                        <p className="mt-3 text-xs text-[#64748B]">Passwords are never displayed after submission. Use at least 12 characters with letters and numbers.</p>
                    </section>

                    {error && <p data-testid="admin-users-error" className="mt-6 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</p>}
                    {message && <p data-testid="admin-users-success" className="mt-6 rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">{message}</p>}

                    <section className="mt-8 rounded-lg border border-[#E2E8F0] bg-white shadow-sm" data-testid="admin-users-list">
                        {users === null ? (
                            <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#0B1120]" /></div>
                        ) : (
                            <div className="divide-y divide-[#E2E8F0]">
                                {users.map((admin) => (
                                    <div key={admin.id} className="grid gap-4 px-5 py-5 lg:grid-cols-[1.3fr_1fr_auto]" data-testid={`admin-user-row-${admin.id}`}>
                                        <div className="min-w-0">
                                            <p className="flex flex-wrap items-center gap-2 text-sm font-bold">
                                                {admin.name || admin.email}
                                                {admin.id === user.id && <span className="rounded-full bg-[#D4AF37]/15 px-2 py-0.5 text-[10px] uppercase tracking-[0.14em] text-[#8a7320]">You</span>}
                                                {admin.disabled && <span className="rounded-full bg-red-50 px-2 py-0.5 text-[10px] uppercase tracking-[0.14em] text-red-700">Disabled</span>}
                                            </p>
                                            <p className="mt-1 truncate text-xs text-[#64748B]">{admin.email}</p>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <select data-testid={`admin-role-select-${admin.id}`} value={admin.admin_role} disabled={admin.id === user.id} onChange={(e) => updateUser(admin.id, { admin_role: e.target.value })} className="w-full rounded-md border border-[#E2E8F0] px-3 py-2.5 text-xs font-semibold outline-none focus:border-[#D4AF37] disabled:bg-[#F8FAFC]">
                                                {ROLES.map((role) => <option key={role} value={role}>{roleLabel(role)}</option>)}
                                            </select>
                                        </div>
                                        <div className="flex flex-wrap items-center gap-2">
                                            <button type="button" data-testid={`admin-disable-${admin.id}`} disabled={admin.id === user.id} onClick={() => updateUser(admin.id, { disabled: !admin.disabled })} className="rounded-md border border-[#E2E8F0] px-3 py-2 text-[10px] font-bold uppercase tracking-[0.12em] text-[#64748B] transition-colors hover:border-[#D4AF37] hover:text-[#0B1120] disabled:opacity-40">
                                                {admin.disabled ? "Enable" : "Disable"}
                                            </button>
                                            <button type="button" data-testid={`admin-reset-${admin.id}`} onClick={() => { setResetFor(admin.id); setResetPassword(""); }} className="rounded-md border border-[#E2E8F0] px-3 py-2 text-[10px] font-bold uppercase tracking-[0.12em] text-[#64748B] transition-colors hover:border-[#D4AF37] hover:text-[#0B1120]">
                                                Reset
                                            </button>
                                            <button type="button" data-testid={`admin-delete-${admin.id}`} disabled={admin.id === user.id} onClick={() => removeUser(admin.id)} aria-label="Remove admin" className="rounded-md border border-red-200 p-2 text-red-700 transition-colors hover:bg-red-50 disabled:opacity-40">
                                                <Trash2 size={15} />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </section>

                    {resetFor && (
                        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#0B1120]/60 px-4" data-testid="admin-reset-modal">
                            <form onSubmit={resetUserPassword} className="w-full max-w-md rounded-lg bg-white p-6 shadow-2xl">
                                <h2 className="flex items-center gap-2 font-adminDisplay text-2xl font-semibold"><UserCog size={20} className="text-[#D4AF37]" /> Reset Admin Password</h2>
                                <p className="mt-2 text-sm text-[#64748B]">Enter a new password. It will be hashed immediately and all existing sessions for that admin will be revoked.</p>
                                <input data-testid="reset-admin-password-input" required type="password" minLength={12} value={resetPassword} onChange={(e) => setResetPassword(e.target.value)} placeholder="New password" className="mt-5 w-full rounded-md border border-[#E2E8F0] px-4 py-3 text-sm outline-none focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/30" autoComplete="new-password" />
                                <div className="mt-5 flex gap-3">
                                    <button type="submit" data-testid="confirm-reset-admin-password" disabled={busy} className="rounded-md bg-[#0B1120] px-5 py-3 text-xs font-bold uppercase tracking-[0.14em] text-white hover:bg-[#1E293B] disabled:opacity-60">Reset Password</button>
                                    <button type="button" data-testid="cancel-reset-admin-password" onClick={() => setResetFor(null)} className="rounded-md border border-[#E2E8F0] px-5 py-3 text-xs font-bold uppercase tracking-[0.14em] text-[#64748B]">Cancel</button>
                                </div>
                            </form>
                        </div>
                    )}

                    <section className="mt-8 rounded-lg border border-[#E2E8F0] bg-white p-6 text-sm text-[#64748B] shadow-sm" data-testid="admin-role-guide">
                        <h2 className="flex items-center gap-2 font-adminDisplay text-xl font-semibold text-[#0F172A]"><ShieldCheck size={18} className="text-[#D4AF37]" /> Role Guide</h2>
                        <div className="mt-4 grid gap-3 sm:grid-cols-2">
                            <p><strong className="text-[#0F172A]">Super Admin:</strong> full system access, admin users and Agent OS.</p>
                            <p><strong className="text-[#0F172A]">Recruitment Admin:</strong> jobs, applications, candidates, employers, recruitment enquiries and alerts.</p>
                            <p><strong className="text-[#0F172A]">Content Admin:</strong> job/content management only.</p>
                            <p><strong className="text-[#0F172A]">Support Admin:</strong> contact enquiries only.</p>
                        </div>
                    </section>
                </main>
            )}
        </div>
    );
}
